import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { Settings, STAGE_DIMENSIONS } from "../settings";
import { PixiSettings } from "../pixiSettings";
import { pauseIosFullscreenState, isIOS, isSafari } from "../utilities";
import { FEATURES_CONFIG } from "../featuresConfig";

// Static grid reference dimensions for UI scaling (never changes per game)
const GRID_SYMBOL_WIDTH = 200;
const GRID_SYMBOL_HEIGHT = 200;

// Responsive breakpoints for layout calculations
const ASPECT_RATIO_BREAKPOINTS = {
  PHONE: 540,
  TABLET: 1024,
  DESKTOP_MIN_WIDTH: 1367,
  DESKTOP_ASPECT_RATIO: 1.56,
  SMALL_DESKTOP_MAX_WIDTH: 1920,
};

// Layout configurations for different device orientations and types
const LAYOUT_CONFIGS = {
  MOBILE_PORTRAIT: {
    paddingSides: 0,
    paddingTop: GRID_SYMBOL_HEIGHT * 4,
    paddingBottom: GRID_SYMBOL_HEIGHT * 3,
  },
  MOBILE_LANDSCAPE: {
    paddingSides: GRID_SYMBOL_WIDTH * 4.5,
    paddingTop: GRID_SYMBOL_HEIGHT * 1.05,
    paddingBottom: GRID_SYMBOL_HEIGHT * 1.05,
  },
  TABLET_PORTRAIT: {
    paddingSides: GRID_SYMBOL_WIDTH * 4.5,
    paddingTop: GRID_SYMBOL_HEIGHT * 6,
    paddingBottom: GRID_SYMBOL_HEIGHT,
  },
  TABLET_LANDSCAPE: {
    paddingSides: GRID_SYMBOL_WIDTH * 5,
    paddingTop: GRID_SYMBOL_HEIGHT * 2.25,
    paddingBottom: GRID_SYMBOL_HEIGHT * 2.25,
  },
  SMALL_DESKTOP: {
    paddingSides: GRID_SYMBOL_WIDTH * 4,
    paddingTop: GRID_SYMBOL_HEIGHT,
    paddingBottom: GRID_SYMBOL_HEIGHT,
  },
  DESKTOP: {
    paddingSides: GRID_SYMBOL_WIDTH * 1.95,
    paddingTop: GRID_SYMBOL_HEIGHT,
    paddingBottom: GRID_SYMBOL_HEIGHT,
  },
};

// Layout type constants (used only within this file)
const LAYOUT_TYPES = {
  MOBILE_PORTRAIT: "MOBILE_PORTRAIT",
  MOBILE_LANDSCAPE: "MOBILE_LANDSCAPE",
  TABLET_PORTRAIT: "TABLET_PORTRAIT",
  TABLET_LANDSCAPE: "TABLET_LANDSCAPE",
  SMALL_DESKTOP: "SMALL_DESKTOP",
  DESKTOP: "DESKTOP",
};

// Device detection thresholds
const DEVICE_DETECTION = {
  SURFACE_DUO_ASPECT_RATIO: 2.5,
  SURFACE_DUO_MIN_DIMENSION: 400,
  LARGE_MOBILE_WIDTH: 900,
  LARGE_MOBILE_MIN_DIMENSION: 600,
  SQUARE_DEVICE_TOLERANCE: 0.25,
  LANDSCAPE_ASPECT_RATIO: 1.1,
};

const DIVIDER_MAX_WIDTH = 4;

export const DimensionsContext = createContext(null);

// Main context provider for responsive layout management
export const DimensionsProvider = ({ children }) => {
  const [windowDimensions, setWindowDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  // Lock dimensions when keyboard is open to prevent game shrinking
  const [lockedDimensions, setLockedDimensions] = useState(null);
  const previousOrientationRef = useRef(null);
  const orientationTimeoutRef = useRef(null);

  // Handle input focus/blur to lock dimensions when keyboard opens
  useEffect(() => {
    const handleFocusIn = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        // Lock current full-screen dimensions when keyboard opens
        setLockedDimensions({
          width: window.innerWidth,
          height: window.innerHeight
        });
        // Store current orientation
        previousOrientationRef.current = window.orientation !== undefined
          ? window.orientation
          : window.screen.orientation?.angle;
      }
    };

    const handleFocusOut = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        // Unlock when keyboard closes
        setLockedDimensions(null);
        previousOrientationRef.current = null;
      }
    };

    document.addEventListener('focusin', handleFocusIn, true);
    document.addEventListener('focusout', handleFocusOut, true);

    return () => {
      document.removeEventListener('focusin', handleFocusIn, true);
      document.removeEventListener('focusout', handleFocusOut, true);
    };
  }, []);

  // Handle orientation changes when keyboard is open
  useEffect(() => {
    const handleOrientationChange = () => {
      const currentOrientation = window.orientation !== undefined
        ? window.orientation
        : window.screen.orientation?.angle;

      // If orientation changed AND dimensions are locked (keyboard open)
      if (lockedDimensions && previousOrientationRef.current !== currentOrientation) {
        // Clear any pending timeout
        if (orientationTimeoutRef.current) {
          clearTimeout(orientationTimeoutRef.current);
        }

        // Temporarily unlock to allow resize
        setLockedDimensions(null);

        // Wait for viewport to stabilize in new orientation
        orientationTimeoutRef.current = setTimeout(() => {
          // Re-lock with NEW orientation's full dimensions
          setLockedDimensions({
            width: window.innerWidth,
            height: window.innerHeight
          });
          previousOrientationRef.current = currentOrientation;
          orientationTimeoutRef.current = null;
        }, 300);
      }
    };

    window.addEventListener('orientationchange', handleOrientationChange);

    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange);
      // Clear timeout on cleanup
      if (orientationTimeoutRef.current) {
        clearTimeout(orientationTimeoutRef.current);
        orientationTimeoutRef.current = null;
      }
    };
  }, [lockedDimensions]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      // If dimensions are locked (keyboard open), skip resize
      if (lockedDimensions) {
        return;
      }

      // Use innerWidth/Height for full screen dimensions (not affected by keyboard)
      const height = window.innerHeight;
      const width = window.innerWidth;

      // Only update state if dimensions actually changed to prevent unnecessary re-renders
      setWindowDimensions((prev) => {
        if (prev.width === width && prev.height === height) {
          return prev; // Return same reference to prevent re-render
        }
        return { width, height };
      });
    };

    window.addEventListener("resize", handleResize);

    // Also listen to visual viewport resize (for iOS toolbar changes)
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", handleResize);
    }

    handleResize(); // Initial call

    return () => {
      window.removeEventListener("resize", handleResize);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", handleResize);
      }
    };
  }, [lockedDimensions]);

  // Calculate effective dimensions, constraining width to max stage width
  // Use locked dimensions if available (keyboard open), otherwise use current window dimensions
  const dimensions = useMemo(() => {
    const source = lockedDimensions || windowDimensions;

    const effectiveWidth = Math.min(
      source.width,
      STAGE_DIMENSIONS.MAX_WIDTH,
    );
    const effectiveHeight = Math.min(
      source.height,
      STAGE_DIMENSIONS.MAX_HEIGHT,
    );

    return {
      width: effectiveWidth,
      height: effectiveHeight,
      windowWidth: source.width,
      windowHeight: source.height,
    };
  }, [lockedDimensions, windowDimensions]);

  // Calculate responsive layout mode based on device type and orientation
  const aspectRatioMode = useMemo(() => {
    const { width, height } = dimensions;
    const aspectRatio = width / height;
    const minDimension = Math.min(width, height);

    let deviceType;
    let layout;
    let orientation;

    // Device detection based on width
    // Desktop requires width >= 1367px (simplified - no aspect ratio requirement)
    if (width >= ASPECT_RATIO_BREAKPOINTS.DESKTOP_MIN_WIDTH) {
      deviceType = PixiSettings.DEVICE.DESKTOP;
    } else if (minDimension <= ASPECT_RATIO_BREAKPOINTS.PHONE) {
      deviceType = PixiSettings.DEVICE.MOBILE;
    } else {
      deviceType = PixiSettings.DEVICE.TABLET;
    }

    // Promote wide mobiles to tablets
    if (deviceType === PixiSettings.DEVICE.MOBILE) {
      const isSurfaceDuo =
        aspectRatio >= DEVICE_DETECTION.SURFACE_DUO_ASPECT_RATIO &&
        minDimension >= DEVICE_DETECTION.SURFACE_DUO_MIN_DIMENSION;
      const isLargeMobile =
        width >= DEVICE_DETECTION.LARGE_MOBILE_WIDTH &&
        minDimension >=
          DEVICE_DETECTION.LARGE_MOBILE_MIN_DIMENSION;

      if (isSurfaceDuo || isLargeMobile) {
        deviceType = PixiSettings.DEVICE.TABLET;
      }
    }

    // Handle near-square devices
    const isSquareDevice =
      Math.abs(aspectRatio - 1.0) <=
      DEVICE_DETECTION.SQUARE_DEVICE_TOLERANCE;

    // Promote mobile square devices to tablet
    if (isSquareDevice && deviceType === PixiSettings.DEVICE.MOBILE) {
      deviceType = PixiSettings.DEVICE.TABLET;
    }

    // Orientation
    let isLandscape;
    // Only force landscape for near-square devices (generous tolerance)
    if (isSquareDevice && aspectRatio >= 0.85) {
      // Near-square devices (aspect ratio 0.8+) use landscape layout
      // This allows up to ~100-150px height difference depending on screen size
      isLandscape = true;
    } else {
      isLandscape =
        aspectRatio > DEVICE_DETECTION.LANDSCAPE_ASPECT_RATIO;
    }

    if (deviceType === PixiSettings.DEVICE.DESKTOP) {
      orientation = PixiSettings.ORIENTATION.LANDSCAPE;
      layout =
        width <= ASPECT_RATIO_BREAKPOINTS.SMALL_DESKTOP_MAX_WIDTH
          ? LAYOUT_TYPES.SMALL_DESKTOP
          : LAYOUT_TYPES.DESKTOP;
    } else {
      orientation = isLandscape
        ? PixiSettings.ORIENTATION.LANDSCAPE
        : PixiSettings.ORIENTATION.PORTRAIT;

      if (deviceType === PixiSettings.DEVICE.MOBILE) {
        layout = isLandscape
          ? LAYOUT_TYPES.MOBILE_LANDSCAPE
          : LAYOUT_TYPES.MOBILE_PORTRAIT;
      } else {
        layout = isLandscape
          ? LAYOUT_TYPES.TABLET_LANDSCAPE
          : LAYOUT_TYPES.TABLET_PORTRAIT;
      }
    }

    return {
      config: LAYOUT_CONFIGS[layout],
      device: deviceType,
      orientation,
      layout,
    };
  }, [dimensions]);

  // Reset iOS fullscreen state when orientation/device changes to portrait or desktop
  useEffect(() => {
    // Only reset if we're on iOS Safari
    if (!isIOS() || !isSafari()) {
      return;
    }

    // Reset if:
    // 1. Orientation is portrait (not landscape)
    // 2. Device is desktop
    const shouldReset =
      aspectRatioMode.orientation === PixiSettings.ORIENTATION.PORTRAIT ||
      aspectRatioMode.device === PixiSettings.DEVICE.DESKTOP;

    if (shouldReset) {
      pauseIosFullscreenState();
    }
  }, [aspectRatioMode.orientation, aspectRatioMode.device]);

  // Calculate symbol dimensions and scaling for the game reel area
  const symbolDimensions = useMemo(() => {
    // Use reelset-specific layout configs with reduced padding for bigger symbols
    let config = PixiSettings.REELSET_LAYOUT_CONFIGS[aspectRatioMode.layout];
    if (!config) {
      // Fallback to minimal padding
      config = { paddingSides: 0, paddingTop: 0, paddingBottom: 0 };
    }
    // const numDividers = Settings.REEL_COUNT - 1;
    const totalDividerWidth = FEATURES_CONFIG.DIVIDERS.ENABLED
      ? (Settings.REEL_COUNT - 1) * DIVIDER_MAX_WIDTH
      : 0;

    const nativeGameWidth =
      Settings.REEL_COUNT * Settings.SYMBOL_WIDTH + totalDividerWidth;
    const nativeGameHeight = Settings.SYMBOLS_VISIBLE * Settings.SYMBOL_HEIGHT;

    const paddingWidth = config.paddingSides;
    const paddingHeight = config.paddingTop + config.paddingBottom;
    const totalNeededWidth = nativeGameWidth + paddingWidth;
    const totalNeededHeight = nativeGameHeight + paddingHeight;

    let scaleFactor = Math.min(
      dimensions.width / totalNeededWidth,
      dimensions.height / totalNeededHeight,
    );

    scaleFactor = Math.min(1, scaleFactor);

    const symbolWidth = Math.round(Settings.SYMBOL_WIDTH * scaleFactor);
    const symbolHeight = Math.round(Settings.SYMBOL_HEIGHT * scaleFactor);

    return {
      symbolWidth,
      symbolHeight,
      scaleFactor,
      totalNeededWidth,
      totalNeededHeight,
      paddingWidth,
      paddingHeight,
    };
  }, [dimensions.width, dimensions.height, aspectRatioMode]);

  const dividerDimensions = useMemo(() => {
    if (!FEATURES_CONFIG.DIVIDERS.ENABLED) {
      return { width: 0, height: 0 };
    }
    const scaledWidth =
      (DIVIDER_MAX_WIDTH * symbolDimensions.symbolWidth) /
      Settings.SYMBOL_WIDTH;
    const width = Math.min(
      DIVIDER_MAX_WIDTH,
      Math.round(scaledWidth)
    );
    const height = symbolDimensions.symbolHeight * Settings.SYMBOLS_VISIBLE;
    return { width, height };
  }, [symbolDimensions.symbolWidth, symbolDimensions.symbolHeight]);

  // UI-specific dimensions using GRID (static reference for UI elements)
  const uiDimensions = useMemo(() => {
    const config = aspectRatioMode.config;
    const numDividers = Settings.REEL_COUNT - 1;
    // Hardcode UI dividers to 8px (original game value) for consistent scaling
    const totalDividerWidth = 8 * numDividers;

    const nativeGameWidth =
      Settings.REEL_COUNT * GRID_SYMBOL_WIDTH + totalDividerWidth;
    // Hardcode UI symbols visible to 3 for consistent toolbar/UI scaling
    const nativeGameHeight = 3 * GRID_SYMBOL_HEIGHT;

    const paddingWidth = config.paddingSides;
    const paddingHeight = config.paddingTop + config.paddingBottom;
    const totalNeededWidth = nativeGameWidth + paddingWidth;
    const totalNeededHeight = nativeGameHeight + paddingHeight;

    let scaleFactor = Math.min(
      dimensions.width / totalNeededWidth,
      dimensions.height / totalNeededHeight,
    );

    scaleFactor = Math.min(1, scaleFactor);

    const symbolWidth = Math.round(GRID_SYMBOL_WIDTH * scaleFactor);
    const symbolHeight = Math.round(GRID_SYMBOL_HEIGHT * scaleFactor);

    return {
      symbolWidth,
      symbolHeight,
      scaleFactor,
      totalNeededWidth,
      totalNeededHeight,
      paddingWidth,
      paddingHeight,
    };
  }, [dimensions.width, dimensions.height, aspectRatioMode]);

  // UI-specific divider dimensions
  const uiDividerDimensions = useMemo(() => {
    const scaledWidth =
      (DIVIDER_MAX_WIDTH * uiDimensions.symbolWidth) /
      GRID_SYMBOL_WIDTH;
    const width = Math.min(
      DIVIDER_MAX_WIDTH,
      Math.round(scaledWidth),
    );
    const height = uiDimensions.symbolHeight * Settings.SYMBOLS_VISIBLE;
    return { width, height };
  }, [uiDimensions.symbolWidth, uiDimensions.symbolHeight]);

  // Responsive configuration helper
  const getResponsiveConfig = useMemo(() => {
    return (config, useUIDimensions = false) => {
      // Choose which dimensions to use
      const dims = useUIDimensions ? uiDimensions : symbolDimensions;
      const baseHeight = useUIDimensions
        ? GRID_SYMBOL_HEIGHT
        : Settings.SYMBOL_HEIGHT;

      let deviceConfig;
      if (aspectRatioMode.device === PixiSettings.DEVICE.DESKTOP) {
        deviceConfig = config.DEVICE_CONFIGS[aspectRatioMode.device];
      } else {
        deviceConfig =
          config.DEVICE_CONFIGS[aspectRatioMode.device]?.[
            aspectRatioMode.orientation
          ]; // Desktop uses landscape orientation by default
      }

      if (!deviceConfig) {
        return {};
      }

      // Merge with defaults if they exist
      if (config.DEFAULTS) {
        deviceConfig = { ...config.DEFAULTS, ...deviceConfig };
      }

      let scaledWidth, scaledHeight;
      const symbolScale = dims.symbolHeight / baseHeight;

      if (config.BASE_PORTRAIT_WIDTH && config.BASE_LANDSCAPE_WIDTH) {
        const shouldUsePortrait =
          (aspectRatioMode.orientation === PixiSettings.ORIENTATION.PORTRAIT &&
            aspectRatioMode.device === PixiSettings.DEVICE.MOBILE) ||
          aspectRatioMode.device === PixiSettings.DEVICE.TABLET;

        const baseWidth = shouldUsePortrait
          ? config.BASE_PORTRAIT_WIDTH
          : config.BASE_LANDSCAPE_WIDTH;
        const baseHeightValue = shouldUsePortrait
          ? config.BASE_PORTRAIT_HEIGHT
          : config.BASE_LANDSCAPE_HEIGHT;

        scaledWidth = Math.round(
          baseWidth * deviceConfig.deviceMultiplier * symbolScale,
        );
        scaledHeight = Math.round(
          baseHeightValue * deviceConfig.deviceMultiplier * symbolScale,
        );
      } else {
        scaledWidth = Math.round(
          config.BASE_WIDTH * deviceConfig.deviceMultiplier * symbolScale,
        );
        scaledHeight = Math.round(
          config.BASE_HEIGHT * deviceConfig.deviceMultiplier * symbolScale,
        );
      }

      let fontSize;
      if (config.FONT_SIZE_RATIO) {
        fontSize = Math.floor(scaledHeight * config.FONT_SIZE_RATIO);
      }

      return {
        width: scaledWidth,
        height: scaledHeight,
        fontSize,
        ...deviceConfig,
      };
    };
  }, [
    aspectRatioMode.device,
    aspectRatioMode.orientation,
    symbolDimensions,
    uiDimensions,
  ]);

  // Memoize derived values to prevent recalculation
  const gameScale = useMemo(
    () => symbolDimensions.symbolHeight / Settings.SYMBOL_HEIGHT,
    [symbolDimensions.symbolHeight],
  );
  const uiScale = useMemo(
    () => uiDimensions.symbolHeight / GRID_SYMBOL_HEIGHT,
    [uiDimensions.symbolHeight],
  );
  const reelSpacing = useMemo(
    () => symbolDimensions.symbolWidth + dividerDimensions.width,
    [symbolDimensions.symbolWidth, dividerDimensions.width],
  );
  const uiReelSpacing = useMemo(
    () => uiDimensions.symbolWidth + uiDividerDimensions.width,
    [uiDimensions.symbolWidth, uiDividerDimensions.width],
  );
  const isMobilePortrait = useMemo(
    () =>
      aspectRatioMode.device === PixiSettings.DEVICE.MOBILE &&
      aspectRatioMode.orientation === PixiSettings.ORIENTATION.PORTRAIT,
    [aspectRatioMode.device, aspectRatioMode.orientation],
  );

  // Consolidated context value
  const value = useMemo(() => {
    return {
      dimensions,

      // Game dimensions (adapts to actual SYMBOL_WIDTH/HEIGHT)
      symbolWidth: symbolDimensions.symbolWidth,
      symbolHeight: symbolDimensions.symbolHeight,
      dividerDimensions,
      reelSpacing,
      symbolScale: gameScale,

      // UI dimensions (static based on GRID_SYMBOL_WIDTH/HEIGHT)
      uiSymbolWidth: uiDimensions.symbolWidth,
      uiSymbolSize: uiDimensions.symbolWidth,
      uiSymbolHeight: uiDimensions.symbolHeight,
      uiDividerDimensions,
      uiReelSpacing,
      uiSymbolScale: uiScale,

      device: aspectRatioMode.device,
      orientation: aspectRatioMode.orientation,
      isMobilePortrait,

      getResponsiveConfig,
    };
  }, [
    dimensions,
    symbolDimensions.symbolWidth,
    symbolDimensions.symbolHeight,
    dividerDimensions,
    reelSpacing,
    gameScale,
    uiDimensions.symbolWidth,
    uiDimensions.symbolHeight,
    uiDividerDimensions,
    uiReelSpacing,
    uiScale,
    aspectRatioMode.device,
    aspectRatioMode.orientation,
    isMobilePortrait,
    getResponsiveConfig,
  ]);

  return (
    <DimensionsContext.Provider value={value}>
      {children}
    </DimensionsContext.Provider>
  );
};

// Hook for consuming the DimensionsContext
export const useDimensions = () => {
  const context = useContext(DimensionsContext);
  if (!context) {
    throw new Error("useDimensions must be used within a DimensionsProvider");
  }
  return context;
};
